import {
    DriverReportsResponse,
    ReportsAPI,
    ReportsRequestParams,
    VehicleReportItem
} from '@vromo/api/reportsApi';
import { useStore } from '@vromo/mobx-react/mobx-react';
import { LocalStorage } from '@vromo/models/LocalStorage';
import { useCallback, useState } from 'react';
import { RootStore } from '../../stores/RootStore';
import { downloadCSV as downloadCSVFromLink } from '../helpers';
import { Filters } from '../utils/reportTypes';
import { Rate } from './helpers';

interface Props {
    filters: Filters;
    filterRates: Rate;
    groupId: number | null | undefined;
}

interface OutputProps {
    applyFilters: (pageNum?: number) => Promise<void>;
    downloadCSV: (csvTitle: string) => Promise<void>;
    isLoading: boolean;
    isLoadingCSV: boolean;
    pageNumber: number;
    totalPages: number;
    vehicleReports: VehicleReportItem[] | null;
}

const MAX_PAGE_SIZE = 2000;

const useReports = ({ filters, filterRates, groupId }: Props): OutputProps => {
    const [isLoading, setLoading] = useState<boolean>(false);
    const [isLoadingCSV, setLoadingCSV] = useState<boolean>(false);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [vehicleReports, setVehicleReports] = useState<VehicleReportItem[] | null>(null);

    const {
        authStore: { user, token },
        viewStore: {imperialUnits}
    } = useStore<RootStore>('rootStore');
    const { vehicleStore } = useStore<RootStore>('rootStore');

    const buildRequestParams = (newPageNumber?: number) => {
        const { startDate, endDate, vehicles, teams } = filters;
        const { fixed, perCompletedJob, perCancelled, perKmJob, perKmEmpty } = filterRates;
        const requestParams: ReportsRequestParams = {
            ['group-id']: groupId,
            ['time-from']: startDate.getTime(),
            ['time-to']: endDate.getTime(),
            fixed: fixed || 0,
            ['per-complete-job']: perCompletedJob || 0,
            ['per-cancelled']: perCancelled || 0,
            ['per-km-on-job']: perKmJob || 0,
            ['per-km-off-job']: perKmEmpty || 0,
            page: newPageNumber || 0,
            size: MAX_PAGE_SIZE,
            isImperial: imperialUnits || false
        };

        requestParams.drivers = vehicles.map(
            (filterObject) => vehicleStore.withId(filterObject?.value as number)?.driver?.id || 0
        );

        requestParams.groups = teams.map((filterObject) => filterObject?.value as number);
        return requestParams;
    };

    const applyFilters = useCallback(
        async (newPageNumber?: number) => {
            try {
                LocalStorage.driverReportPayRates = filterRates;

                if (!groupId || !token) return;

                setLoading(true);
                setVehicleReports(null);
                setTotalPages(1);

                const api = new ReportsAPI(user.apiHost);
                const requestParams = buildRequestParams(newPageNumber);

                const reportsResponse: DriverReportsResponse | null =
                    await api.getDriverReports.get(requestParams, token);

                if (reportsResponse === null) return;

                const {
                    _embedded: { vehicleReports },
                    page
                } = reportsResponse as DriverReportsResponse;

                setVehicleReports(vehicleReports);
                setTotalPages(page.totalPages);
                setPageNumber(page.number);
            } catch (e) {
                console.warn('Error: Failed to apply report filters: ', e);
            } finally {
                setLoading(false);
            }
        },
        [filterRates, filters, groupId, token, user.apiHost, vehicleStore]
    );

    const downloadCSV = async (csvTitle: string) => {
        setLoadingCSV(true);
        if (!token) return;
        const api = new ReportsAPI(user.apiHost);
        const requestParams = buildRequestParams(pageNumber);

        try {
            const csvResponse = await api.getDriverReportsCSV.get(requestParams, token);
            if (!csvResponse) return;
            downloadCSVFromLink(csvResponse.reportDownloadLink as string, csvTitle);
            setLoadingCSV(false);
        } catch (e) {
            setLoadingCSV(false);
        }
    };

    return {
        applyFilters,
        downloadCSV,
        isLoading,
        isLoadingCSV,
        pageNumber,
        totalPages,
        vehicleReports
    };
};

export default useReports;
